
import Vue, { PropType } from 'vue';
import { CvFileUploader } from '@carbon/vue';
import {
  Artwork,
  ArtworkResult,
  VFileUploader,
  VFileUploaderFile,
} from './index';
import { supportedExtensions } from '@/constants/supportedExtensions';
import { ImageProcessingEngineVersion } from '@/constants/imageProcessingEngine';
import { AxiosError } from 'axios';

export default Vue.extend({
  components: { CvFileUploader },
  inheritAttrs: false,
  props: {
    uploadOptions: {
      type: Object as PropType<Artwork>,
      required: true,
    },

    api: {
      type: Object as PropType<{
        upload: (data: Artwork, file: VFileUploaderFile) => Promise<unknown>;
      }>,
      required: true,
    },

    apiOptions: {
      type: Object as PropType<{
        lazy: boolean;
      }>,
      default: () => ({ lazy: false }),
    },

    id: {
      type: String,
      required: true,
    },

    resetState: {
      type: Boolean,
      required: false,
      default: false,
    },

    validateFn: {
      type: Function as PropType<(props: Record<string, unknown>) => true>,
      required: true,
    },

    helperText: {
      type: String,
      default: `
          You can specify a file to import by either dragging it into the drag and
          drop area or by selecting a file from your computer. Maximum file size of
          50MB (.jpg, .png, .gif, .jpeg, .pdf, .eps, .ico, .bmp, .tif, .ai, .psd files only)
      `,
    },
  },

  data() {
    return {
      /**
       * Reference to CvFileUploader component
       */
      fileUploaderRef: null as unknown,
      files: [] as unknown as VFileUploaderFile[],
      supportedExtensions,
      maxFileSize: 50,
    };
  },

  computed: {
    isEmptyInternalFiles(): boolean {
      return (
        (this.fileUploaderRef as VFileUploader)?.internalFiles.length === 0
      );
    },
    /**
     * This will be the flag, for file-uploader--disabled class
     * to appear, so that we can scope disabled styles in this state
     */
    isDisabled(): boolean {
      return this.$attrs.disabled as unknown as boolean;
    },
  },

  watch: {
    resetState() {
      if (this.resetState) {
        this.files = [];
        (this.fileUploaderRef as VFileUploader).remove(0);
      }
    },
  },

  mounted() {
    (this.fileUploaderRef as VFileUploader) = this.$refs
      ?.fileUploader as VFileUploader;
  },

  methods: {
    async upload(files: VFileUploaderFile[]) {
      const uploadResults = [] as ArtworkResult[];
      let i = 0;
      for (const file of files) {
        /**
         * Safe check, in case when no file is present
         */
        if (!file) {
          this.$emit('change', files);
          return;
        }

        const {
          title,
          externalCustomerId,
          customerId,
          shouldSaveInLogoLibrary,
          shouldSaveDraftForWizard,
        } = this.uploadOptions;

        var titleFromFileName = file.file.name;
        var defaultTitle = titleFromFileName.replace(
          titleFromFileName.substr(titleFromFileName.lastIndexOf('.')),
          ''
        );

        this.setState(i, 'uploading');
        this.$emit('upload:init');

        try {
          const result: { data: ArtworkResult } = (await this.api.upload(
            // first parameter is Artwork model
            {
              title: title || defaultTitle,
              fileName: file.file.name,
              externalCustomerId,
              customerId,
              shouldSaveInLogoLibrary,
              shouldSaveDraftForWizard,
              currentEngineVersion: ImageProcessingEngineVersion,
            },
            // second parameter is file
            {
              file: file.file,
              invalidMessage: file.invalidMessage,
              invalidMessageTitle: file.invalidMessageTitle,
              state: file.state,
            }
          )) as { data: ArtworkResult };
          this.setState(0, 'complete');
          if (!this.isEmptyInternalFiles) {
            this.$emit('upload:success', result);

            result.data.fileName = file.file.name;
            uploadResults.push(result.data);
          }
        } catch (e) {
          this.setState(0, '');
          this.handleProcessArtworkErrors(e as AxiosError);
          this.$emit('upload:error', e);
          throw e;
        }
        i++;
      }

      return uploadResults;
    },

    /**
     * Wrapper around CvFileUploader internal method
     */
    setState(idx: number, state = '') {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setState(idx, state);
    },

    /**
     * Wrapper around CvFileUploader internal method
     */
    setInvalidMessage(idx: number, msg: string) {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setInvalidMessage(idx, msg);
    },

    handleProcessArtworkErrors(error: AxiosError) {
      if (error && error.response?.status === 409) {
        const errorMessage =
          error.response.data.error.message ===
          'Provided file contains only transparent pixels. Please provide different artwork.'
            ? this.$t('section.errors.empty-image').toString()
            : this.$t('section.errors.image-processing').toString();
        this.setInvalidMessage(0, errorMessage);
      } else {
        this.setInvalidMessage(
          0,
          this.$t(
            'upload-logo.inline-notification.something-went-wrong'
          ).toString()
        );
      }
    },

    /**
     * Manipulate uploading state here, $emit events
     */
    async forwardOnChange(files: VFileUploaderFile[]) {
      /**
       * In case of a drag & drop, replace previously dropped in file
       */
      // if (files.length > 1) {
      //   (this.fileUploaderRef as VFileUploader).remove(0);
      //   return;
      // }
      this.$emit('change', files);

      for (const file of files) {
        /**
         * Return early, in case of a file removal
         */
        if (!file) {
          return;
        }

        /**
         * In case we have a file, we proceed with basic validationObj on type & size
         */
        const { size, type } = file.file;
        const validationObj = this.validateFn({
          type: type || 'image/psd',
          size,
          maxFileSize: this.maxFileSize,
          supportedExtensions: this.supportedExtensions,
        });
        const errorAny = Object.values(validationObj).some(
          ({ valid }) => !valid
        );

        if (errorAny) {
          let error = Object.values(validationObj).find(
            ({ valid }: { valid: boolean }) => !valid
          );

          if (error.errorMsg === 'invalid-file-size') {
            this.setInvalidMessage(
              0,
              this.$tc(
                `upload-logo.inline-notification.${error.errorMsg}`,
                error.value,
                { value: error.value }
              ).toString()
            );
          }
          if (error.errorMsg === 'invalid-file-type') {
            this.setInvalidMessage(
              0,
              this.$t(
                `upload-logo.inline-notification.${error.errorMsg}`
              ).toString()
            );
          }

          this.setState(0, '');
          this.$emit('upload:validation-error');
          return;
        }
      }

      /**
       * In case everything is fine, we proceed with file upload
       * when apiOptions.lazy flag isn't being set
       */
      this.files = files; // we need this for scoped slot's usage

      /**
       * Upload immedately if no apiOption.lazy is set to true
       * -> otherwise expose files, and upload() method externally
       * so that consumer can defer a call to this method
       */
      if (!this.apiOptions.lazy) {
        this.upload(files);
      } else {
        this.$emit('upload:ready', { files, up: this.upload });
      }
    },
  },
});
